/**
 * Phase 1.7 — dashboard, v2.
 *
 * Decomposes the v1 monolith (596 lines) into per-widget components. The
 * data fetch is unchanged — same DashboardSummary endpoint, same
 * Promise.all of secondary list endpoints. Composition is what changed.
 *
 * Layout:
 *   WelcomeBanner
 *   KpiStrip (5 cards)
 *   Two-column row: RecentActivityCard | LowStockCard
 *   Right rail: overdue + upcoming tasks
 */

import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { authFetch, useAuthStore } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { WelcomeBanner } from '@/components/v2';
import { RightRail } from '@/components/v2/layout';
import { usePageHeader } from '@/components/v2/layout/Header';
import { KpiStrip } from '@/components/v2/dashboard/KpiStrip';
import { RecentActivityCard } from '@/components/v2/dashboard/RecentActivityCard';
import { LowStockCard } from '@/components/v2/dashboard/LowStockCard';
import { TodayTasksRail } from '@/components/v2/dashboard/TodayTasksRail';
import type { DashboardSummary } from '@shared/types';

export function HomePageV2(): JSX.Element {
  usePageHeader(
    'Dashboard',
    <Button
      asChild
      size="sm"
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <Link to="/logs">
        <Plus className="h-4 w-4" />
        Log activity
      </Link>
    </Button>,
  );
  return <HomePageV2Body />;
}

/**
 * Page body separated so AuthGuardV2's `rail` prop can pull a JSX node out
 * of the same render. The page itself is rendered inside AppShell, so
 * the rail prop on AppShell receives <DashboardWithRail/> output split.
 */
function HomePageV2Body(): JSX.Element {
  const userName = useAuthStore((s) => s.user?.name ?? 'Rancher');
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const s = await authFetch<DashboardSummary>('/api/dashboard/summary');
        if (!cancelled) setSummary(s);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="lg:flex lg:items-start lg:gap-6">
      <div className="min-w-0 flex-1 space-y-6">
        <WelcomeBanner
          name={userName.split(' ')[0]}
          subtitle={
            summary
              ? `${summary.openTaskCount} open tasks • ${summary.pendingDocCount} docs to review`
              : 'Loading…'
          }
        />
        {error ? (
          <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
            {error}
          </div>
        ) : null}
        {loading || !summary ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-28 rounded-xl" />
            ))}
          </div>
        ) : (
          <KpiStrip summary={summary} />
        )}
        {loading || !summary ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Skeleton className="h-64 rounded-xl" />
            <Skeleton className="h-64 rounded-xl" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <RecentActivityCard events={summary.recentActivity.events} />
            <LowStockCard items={summary.lowStockItems} />
          </div>
        )}
      </div>
      {summary ? (
        <RightRail className="mt-6 lg:mt-0">
          <TodayTasksRail summary={summary} />
        </RightRail>
      ) : null}
    </div>
  );
}
